import { useEffect, useState } from 'react';
import { LOCAL_STORAGE_NAMES } from '../constants/constants';
import { useUser } from '../context/UserContext';

export interface IDefaultViewPreference {
  matters: boolean;
  customers: boolean;
}

const DEFAULT_PREFERENCE: IDefaultViewPreference = { matters: true, customers: true };

const readPreference = (userId?: string): IDefaultViewPreference => {
  if (!userId) {
    return DEFAULT_PREFERENCE;
  }
  const stored = localStorage.getItem(LOCAL_STORAGE_NAMES.defaultViewPreference);
  const allUsersPreferences = stored ? JSON.parse(stored) : {};
  return allUsersPreferences[userId] ?? DEFAULT_PREFERENCE;
};

const writePreference = (
  userId: string,
  updates: Partial<IDefaultViewPreference>,
): IDefaultViewPreference => {
  const stored = localStorage.getItem(LOCAL_STORAGE_NAMES.defaultViewPreference);
  const allUsersPreferences = stored ? JSON.parse(stored) : {};
  const merged = { ...(allUsersPreferences[userId] ?? DEFAULT_PREFERENCE), ...updates };
  allUsersPreferences[userId] = merged;
  localStorage.setItem(
    LOCAL_STORAGE_NAMES.defaultViewPreference,
    JSON.stringify(allUsersPreferences),
  );
  return merged;
};

export const useDefaultViewPreference = () => {
  const { user } = useUser();
  const [preference, setPreference] = useState<IDefaultViewPreference>(() =>
    readPreference(user?.id),
  );

  useEffect(() => {
    setPreference(readPreference(user?.id));
  }, [user?.id]);

  const updatePreference = (updates: Partial<IDefaultViewPreference>) => {
    if (!user?.id) {
      return;
    }
    setPreference(writePreference(user.id, updates));
  };

  return { preference, updatePreference };
};
